import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ASSET_STATUS_MAP,
  DEPRECIATION_METHOD_MAP,
  type AssetStatus,
  type DepreciationMethod,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { date, dateTime, money } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';
import { Badge, ErrorState, Modal, PageHead } from '../../components/ui';
import { RecordStats, type RecordStat } from '../../components/RecordStats';
import { Attachments } from '../../components/Attachments';

interface Detail {
  id: string;
  code: string;
  name: string;
  description: string | null;
  serialNumber: string | null;
  location: string | null;
  status: AssetStatus;
  purchaseDate: string;
  purchaseCost: number;
  salvageValue: number;
  accumulatedDepreciation: number;
  bookValue: number;
  monthlyDepreciation: number;
  method: DepreciationMethod;
  rate: number;
  usefulLifeMonths: number;
  lastDepreciatedPeriod: string | null;
  disposalDate: string | null;
  disposalProceeds: number | null;
  disposalNote: string | null;
  custodianId: string | null;
  category: { id: string; name: string };
  custodian: { id: string; name: string; jobTitle: string | null } | null;
  supplier: { id: string; name: string } | null;
  entries: Array<{ id: string; period: string; amount: number; bookValueAfter: number }>;
  custodyLog: Array<{
    id: string;
    employeeId: string | null;
    employeeName: string | null;
    note: string | null;
    at: string;
  }>;
}

/**
 * ما تعرفه شاشة الأصل عنه.
 *
 * المحاسب يريد القيمة الدفترية ومجمع الإهلاك، ومن يجرد المخزون يريد الموقع
 * والرقم التسلسلي — وكلاهما يفتح هذه الشاشة.
 */
function assetCards(a: any, retired: boolean): RecordStat[] {
  const months = Math.floor(
    (Date.now() - new Date(a.purchaseDate).getTime()) / (30.44 * 86_400_000),
  );

  return [
    {
      key: 'status',
      label: 'الحالة',
      group: 'الأصل',
      icon: 'clipboard',
      color: retired ? '#9BA3AE' : '#51A521',
      value: (
        <Badge kind={ASSET_STATUS_MAP[a.status as AssetStatus].kind}>
          {ASSET_STATUS_MAP[a.status as AssetStatus].label}
        </Badge>
      ),
      hint: 'في الخدمة أم خرج منها.',
    },
    {
      key: 'age',
      label: 'العمر',
      group: 'الأصل',
      icon: 'clock',
      color: '#8791A1',
      value: months >= 12 ? `${Math.floor(months / 12)} سنة` : `${months} شهرًا`,
      numeric: true,
      note: date(a.purchaseDate),
      hint: 'منذ متى والشركة تملكه.',
    },
    {
      key: 'location',
      label: 'الموقع',
      group: 'الأصل',
      icon: 'home',
      color: '#6F7784',
      value: a.location || '—',
      note: a.location ? undefined : 'لم يُسجَّل',
      hint: 'أين يوجد — وهو ما يُجرَد عليه.',
    },
    {
      key: 'serial',
      label: 'الرقم التسلسلي',
      group: 'الأصل',
      icon: 'search',
      color: '#9BA3AE',
      value: a.serialNumber || '—',
      numeric: !!a.serialNumber,
      note: a.code,
      hint: 'رقم الشركة المصنّعة — يميّزه عن مثيله.',
    },
    {
      key: 'supplier',
      label: 'المورد',
      group: 'الأصل',
      icon: 'building',
      color: '#3672EA',
      value: a.supplier?.name ?? '—',
      hint: 'من اشتُري منه.',
    },

    {
      key: 'purchaseCost',
      label: 'التكلفة',
      group: 'المالية',
      icon: 'wallet',
      color: '#1D46A6',
      value: money(a.purchaseCost),
      numeric: true,
      note: date(a.purchaseDate),
      hint: 'ما دُفع فيه يوم الشراء.',
    },
    {
      key: 'accumulated',
      label: 'مجمّع الإهلاك',
      group: 'المالية',
      icon: 'chart',
      color: '#DC2626',
      value: <span className="neg">{money(a.accumulatedDepreciation)}</span>,
      numeric: true,
      hint: 'ما استُهلك من قيمته حتى اليوم.',
    },
    {
      key: 'bookValue',
      label: 'القيمة الدفترية',
      group: 'المالية',
      icon: 'book',
      color: '#51A521',
      value: <span className="strong">{money(a.bookValue)}</span>,
      numeric: true,
      note: 'التكلفة ناقص المجمّع',
      hint: 'قيمته في الدفاتر الآن.',
    },
    {
      key: 'salvage',
      label: 'قيمة الخردة',
      group: 'المالية',
      icon: 'bank',
      color: '#8791A1',
      value: money(a.salvageValue),
      numeric: true,
      note: 'ما لا يُهلَك منه',
      hint: 'ما يُتوقّع بيعه به في نهاية عمره — ولا يدخل في الإهلاك.',
    },

    {
      key: 'monthly',
      label: 'القسط الشهري',
      group: 'الإهلاك',
      icon: 'refresh',
      color: retired ? '#9BA3AE' : '#BF930B',
      /* الخارج من الخدمة لا يُهلَك — والبطاقة تقولها بدل أن تختفي. */
      value: retired ? 'توقّف' : money(a.monthlyDepreciation),
      numeric: !retired,
      note: retired ? 'خرج من الخدمة' : 'يُرحَّل آخر كل شهر',
      hint: 'ما يُحمَّل على المصروف كل شهر.',
    },
    {
      key: 'method',
      label: 'طريقة الإهلاك',
      group: 'الإهلاك',
      icon: 'chartBar',
      color: '#3672EA',
      value: DEPRECIATION_METHOD_MAP[a.method as DepreciationMethod].label,
      note:
        a.method === 'DECLINING'
          ? `${a.rate}٪ سنويًّا`
          : `${a.usefulLifeMonths} شهرًا`,
      hint: 'كيف تُوزَّع التكلفة على العمر الإنتاجي.',
    },
    {
      key: 'lastPeriod',
      label: 'آخر شهر مُهلَك',
      group: 'الإهلاك',
      icon: 'calendar',
      color: a.lastDepreciatedPeriod ? '#5596FF' : '#9BA3AE',
      value: a.lastDepreciatedPeriod ? a.lastDepreciatedPeriod.slice(0, 7) : 'لم يبدأ',
      numeric: !!a.lastDepreciatedPeriod,
      hint: 'إلى أين وصل الترحيل — وما بعده لم يُحمَّل بعد.',
    },
  ];
}

export function AssetDetail() {
  const { id = '' } = useParams();
  const { atLeast } = useAuth();
  const queryClient = useQueryClient();
  const [assigning, setAssigning] = useState(false);
  const [disposing, setDisposing] = useState(false);

  const canEdit = atLeast('ADMIN');

  const detail = useQuery({
    queryKey: ['assets', id],
    queryFn: () => api<Detail>(`/assets/${id}`),
  });

  if (detail.isError) return <ErrorState error={detail.error} onRetry={() => detail.refetch()} />;
  const a = detail.data;
  const retired = a ? ASSET_STATUS_MAP[a.status].retired : false;

  return (
    <div className="page">
      <PageHead
        title={a?.name ?? 'أصل'}
        subtitle={a ? `${a.code} — ${a.category.name}` : undefined}
        actions={
          a && (
            <>
              <Link to="/assets" className="btn">
                رجوع
              </Link>
              {!retired && (
                <button className="btn" onClick={() => setAssigning(true)}>
                  {a.custodian ? 'نقل العهدة' : 'تسليم عهدة'}
                </button>
              )}
              {canEdit && !retired && (
                <button className="btn btn-danger" onClick={() => setDisposing(true)}>
                  استبعاد
                </button>
              )}
            </>
          )
        }
      />

      {a && (
        <>
          <RecordStats
            items={assetCards(a, retired)}
            storageKey="goyone.stats.asset"
            /* الافتراضي: ما كان معروضًا قبل أن يصير الاختيار متاحًا. */
            defaults={[
              'status',
              'purchaseCost',
              'accumulated',
              'bookValue',
              'monthly',
            ]}
          />

          {retired && (
            <div className="alert alert-info mb-4">
              خرج من الخدمة في {a.disposalDate ? date(a.disposalDate) : '—'}
              {a.disposalProceeds !== null && ` مقابل ${money(a.disposalProceeds)}`}.
              {a.disposalNote && ` ${a.disposalNote}`} تاريخه محفوظ ولا يُحذف: له إهلاك مرحَّل في
              الدفتر.
            </div>
          )}

          <div className="builder">
            <div className="card">
              <div className="card-head">
                <h2 className="card-title">البيانات</h2>
              </div>
              <div className="card-body">
                <div className="form-grid">
                  <Field label="الكود" value={a.code} num />
                  <Field label="الرقم التسلسلي" value={a.serialNumber} num />
                  <Field label="الموقع" value={a.location} />
                  <Field label="تاريخ الشراء" value={date(a.purchaseDate)} num />
                  <Field label="قيمة الخردة" value={money(a.salvageValue)} num />
                  <Field label="المورد" value={a.supplier?.name ?? null} />
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-head">
                <h2 className="card-title">سياسة الإهلاك</h2>
              </div>
              <div className="card-body">
                <div className="totals-box">
                  <div className="totals-row">
                    <span className="mute">الطريقة</span>
                    <span>{DEPRECIATION_METHOD_MAP[a.method].label}</span>
                  </div>
                  {a.method === 'DECLINING' && (
                    <div className="totals-row">
                      <span className="mute">النسبة السنوية</span>
                      <span className="num">{a.rate}٪</span>
                    </div>
                  )}
                  {a.method === 'STRAIGHT_LINE' && (
                    <div className="totals-row">
                      <span className="mute">العمر الإنتاجي</span>
                      <span className="num">{a.usefulLifeMonths} شهرًا</span>
                    </div>
                  )}
                  <div className="totals-row">
                    <span className="mute">آخر شهر مُهلَك</span>
                    <span className="num">
                      {a.lastDepreciatedPeriod ? a.lastDepreciatedPeriod.slice(0, 7) : 'لم يبدأ'}
                    </span>
                  </div>
                  <div className="totals-row totals-final">
                    <span>القسط الشهري القادم</span>
                    <span className="num">{money(a.monthlyDepreciation)}</span>
                  </div>
                </div>

                <p className="tiny mute mt-3">
                  {DEPRECIATION_METHOD_MAP[a.method].description}
                </p>
              </div>
            </div>
          </div>

          <div className="builder mt-4">
            <div className="card">
              <div className="card-head">
                <h2 className="card-title">سجل العهدة</h2>
              </div>
              <div className="table-wrap">
                <table className="table">
                  <tbody>
                    {a.custodyLog.map((l) => (
                      <tr key={l.id}>
                        <td>
                          <span className="cell-strong">
                            {l.employeeName ?? 'المخزن'}
                          </span>
                          {l.note && <div className="tiny mute">{l.note}</div>}
                        </td>
                        <td className="num cell-mute">{dateTime(l.at)}</td>
                      </tr>
                    ))}
                    {a.custodyLog.length === 0 && (
                      <tr>
                        <td className="cell-mute">لا حركة عهدة مسجَّلة.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="card">
              <div className="card-head">
                <h2 className="card-title">أقساط الإهلاك</h2>
              </div>
              <div className="table-wrap">
                <table className="table">
                  <tbody>
                    {a.entries.map((e) => (
                      <tr key={e.id}>
                        <td className="cell-strong num">{e.period.slice(0, 7)}</td>
                        <td className="num neg">{money(e.amount)}</td>
                        <td className="num cell-mute">{money(e.bookValueAfter)}</td>
                      </tr>
                    ))}
                    {a.entries.length === 0 && (
                      <tr>
                        <td className="cell-mute">لم يُرحَّل إهلاك بعد.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {assigning && a && (
        <CustodyDialog
          asset={a}
          onClose={() => setAssigning(false)}
          onDone={() => {
            void queryClient.invalidateQueries({ queryKey: ['assets'] });
            setAssigning(false);
          }}
        />
      )}

      {disposing && a && (
        <DisposeDialog
          asset={a}
          onClose={() => setDisposing(false)}
          onDone={() => {
            void queryClient.invalidateQueries({ queryKey: ['assets'] });
            setDisposing(false);
          }}
        />
      )}
      <div className="mt-4">
        <Attachments entity="asset" entityId={id} title="مستندات الأصل" />
      </div>

    </div>
  );
}

function Field({ label, value, num }: { label: string; value: string | null; num?: boolean }) {
  return (
    <div className="field">
      <span className="label">{label}</span>
      <span className={num ? 'num' : undefined} dir={num ? 'ltr' : undefined}>
        {value || '—'}
      </span>
    </div>
  );
}

/**
 * تسليم العهدة.
 *
 * كل حركة تُسجَّل في السجل، تسليمًا كانت أو ردًّا: حقل الحائز الحالي وحده
 * لا يجيب عن "من كان في عهدته الشهر الماضي" — وهو السؤال الذي يُطرح حين
 * يُفقد الأصل.
 */
function CustodyDialog({
  asset,
  onClose,
  onDone,
}: {
  asset: Detail;
  onClose: () => void;
  onDone: () => void;
}) {
  const [employeeId, setEmployeeId] = useState(asset.custodianId ?? '');
  const [note, setNote] = useState('');

  const employees = useQuery({
    queryKey: ['employees', { pageSize: 200, status: 'ACTIVE' }],
    queryFn: () =>
      api<{ items: Array<{ id: string; name: string }> }>(
        '/hr/employees?pageSize=200&status=ACTIVE',
      ),
  });

  const assign = useMutation({
    mutationFn: () =>
      api(`/assets/${asset.id}/custody`, {
        method: 'POST',
        body: { employeeId: employeeId || null, note: note || null },
      }),
    onSuccess: onDone,
  });

  return (
    <Modal
      title="العهدة"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => assign.mutate()}
            disabled={assign.isPending}
          >
            {assign.isPending ? 'جارٍ الحفظ' : 'حفظ'}
          </button>
        </>
      }
    >
      {assign.isError && (
        <div className="alert alert-danger mb-4">
          {assign.error instanceof Error ? assign.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      <div className="field">
        <label className="label" htmlFor="c-employee">
          في عهدة
        </label>
        <select
          id="c-employee"
          className="select"
          value={employeeId}
          onChange={(e) => setEmployeeId(e.target.value)}
        >
          <option value="">المخزن — ردّ العهدة</option>
          {employees.data?.items.map((e) => (
            <option key={e.id} value={e.id}>
              {e.name}
            </option>
          ))}
        </select>
      </div>

      <div className="field mt-4">
        <label className="label" htmlFor="c-note">
          ملاحظة
        </label>
        <input
          id="c-note"
          className="input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="سبب النقل أو حالة الجهاز"
        />
      </div>

      <div className="alert alert-info mt-4">
        من في عهدته أصول لا تنتهي خدمته حتى يسلّمها. تُسجَّل كل حركة في سجل الأصل.
      </div>
    </Modal>
  );
}

function DisposeDialog({
  asset,
  onClose,
  onDone,
}: {
  asset: Detail;
  onClose: () => void;
  onDone: () => void;
}) {
  const [disposalDate, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [proceeds, setProceeds] = useState('');
  const [writeOff, setWriteOff] = useState(false);
  const [note, setNote] = useState('');

  const dispose = useMutation({
    mutationFn: () =>
      api<{ result: number }>(`/assets/${asset.id}/dispose`, {
        method: 'POST',
        body: {
          date: disposalDate,
          proceeds: writeOff ? 0 : Number(proceeds) || 0,
          writeOff,
          note: note || null,
        },
      }),
    onSuccess: onDone,
  });

  const amount = writeOff ? 0 : Number(proceeds) || 0;
  const result = amount - asset.bookValue;

  return (
    <Modal
      title="استبعاد الأصل"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-danger"
            onClick={() => dispose.mutate()}
            disabled={dispose.isPending}
          >
            {dispose.isPending ? 'جارٍ' : 'استبعاد'}
          </button>
        </>
      }
    >
      {dispose.isError && (
        <div className="alert alert-danger mb-4">
          {dispose.error instanceof Error ? dispose.error.message : 'تعذّرت العملية.'}
        </div>
      )}

      <div className="form-grid">
        <div className="field">
          <label className="label req" htmlFor="x-date">
            التاريخ
          </label>
          <input
            id="x-date"
            className="input num"
            type="date"
            value={disposalDate}
            onChange={(e) => setDate(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="x-proceeds">
            ما قُبض من بيعه
          </label>
          <input
            id="x-proceeds"
            className="input num"
            dir="ltr"
            inputMode="decimal"
            value={proceeds}
            onChange={(e) => setProceeds(e.target.value)}
            disabled={writeOff}
          />
        </div>

        <div className="field span-2">
          <label className="check">
            <input
              type="checkbox"
              checked={writeOff}
              onChange={(e) => setWriteOff(e.target.checked)}
            />
            <span>إعدام بلا مقابل</span>
          </label>
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="x-note">
            ملاحظة
          </label>
          <input
            id="x-note"
            className="input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      </div>

      <div className="totals-box mt-4">
        <div className="totals-row">
          <span className="mute">القيمة الدفترية</span>
          <span className="num">{money(asset.bookValue)}</span>
        </div>
        <div className="totals-row">
          <span className="mute">ما يُقبض</span>
          <span className="num">{money(amount)}</span>
        </div>
        <div className="totals-row totals-final">
          <span>{result >= 0 ? 'ربح الاستبعاد' : 'خسارة الاستبعاد'}</span>
          <span className={`num ${result >= 0 ? 'pos' : 'neg'}`}>{money(Math.abs(result))}</span>
        </div>
      </div>

      {/*
        لا يُحذف الأصل: له إهلاك مرحَّل في الدفتر العام، وحذفه يقطع أرقامًا
        مرحَّلة عن مصدرها.
      */}
      <div className="alert alert-info mt-4">
        يخرج من الخدمة ويتوقف إهلاكه، ويبقى تاريخه وأقساطه المرحَّلة كما هي.
      </div>
    </Modal>
  );
}
